import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import {
  withApi,
  withBackend,
  withIntl,
  withNotifier,
  backendShape,
  intlShape,
  ActionBar,
  Loader,
  Column,
  Row,
  withFlipper,
  EditActionButton,
  apiShape,
  notifierShape,
  SubmitButton,
  useTracking,
  sessionShape,
  withSession,
} from "lcm-iot-commons";
import { FormattedMessage } from "react-intl";
import CONFIGURATION from "lcm-iot-commons/client/lib/configuration";

type Props = {
  readonly backend: backendShape;
  readonly api: apiShape;
  readonly intl: intlShape;
  readonly notifier: notifierShape;
  readonly flipper: {
    readonly manageMyEndressMfa?: boolean;
  };
  readonly session: sessionShape;
};

export function MyEndressMfaDetails({
  backend,
  intl,
  api,
  notifier,
  flipper,
  session,
}: Props) {
  if (flipper.manageMyEndressMfa) {
    const [isLoading, setIsLoading] = useState(true);
    const [userMFAStatus, setUserMFAStatus] = useState();
    const [isMFAEnabled, setIsMFAEnabled] = useState(
      userMFAStatus === "enabled",
    );
    const [showModel, setShowModel] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const tracking = useTracking();
    const loadUser = async () => {
      try {
        const user = await api.get("/users/current/", {}, false);
        setUserMFAStatus(user.otp_module);
      } catch (error) {
        notifier.showError(api.translateError(error));
      }
    };

    const loadMFAStatus = async () => {
      try {
        const response = await backend.get("/users/mfa_status");
        setIsMFAEnabled(response.mfa_enabled);
      } catch (error) {
        notifier.showError(backend.translateError(error));
      }
      setIsLoading(false);
    };

    const handleMFAChange = async (mfaLabel: string) => {
      setIsSubmitting(true);
      try {
        const actionName = mfaLabel.toLowerCase();
        const response = await backend.patch(
          `/users/update_mfa?action_name=${actionName}`,
        );
        setIsMFAEnabled(response.mfa_enabled);
        if (response.mfa_enabled === true) {
          setShowModel(false);
          notifier.showSuccess(
            intl.formatMessage({ id: "mfa.enabled_success_message" }),
          );
          setTimeout(() => {
            tracking.reset();
            session?.deleteSession?.();
            const myEndressLogoutUrl = session?.myEndressSsoLogoutUrl;
            const netilionLogoutUrl = `${globalThis.location.origin}${CONFIGURATION.BASE_URL}/logout`;
            globalThis.location.href = `${myEndressLogoutUrl}?post_logout_redirect_uri=${netilionLogoutUrl}`;
          }, 1000);
        } else {
          notifier.showSuccess(
            intl.formatMessage({ id: "mfa.disable_mfa_request_message" }),
          );
          setShowModel(false);
        }
      } catch (error) {
        notifier.showError(backend.translateError(error));
      } finally {
        setIsSubmitting(false);
        setIsLoading(false);
      }
    };

    React.useEffect(() => {
      loadMFAStatus();
      loadUser();
    }, []);

    const status = isMFAEnabled ? "enabled" : "disabled";

    const icon = isMFAEnabled ? (
      <i className="icon3-eh-radio-button-checked mfa-icon mfa-enabled-icon" />
    ) : (
      <i className="icon-eh-namur-failure mfa-icon mfa-disabled-icon" />
    );
    const message = (
      <div className="mfa-message">
        {icon}
        {intl.formatMessage({ id: `mfa.${status}_header` })}
      </div>
    );

    const disabledTip =
      status === "disabled" ? (
        <Row>
          <Column id="mfa-tip" className="col-md-12 mfa-tip">
            {intl.formatMessage({ id: "mfa.tip" })}
          </Column>
        </Row>
      ) : null;

    const mfaLabel = isMFAEnabled
      ? intl.formatMessage({ id: "mfa.myendress.disable_button" })
      : intl.formatMessage({ id: "mfa.myendress.enable_button" });

    const modelConfirmationHeader = isMFAEnabled
      ? intl.formatMessage({ id: "mfa.myendress.disable_confirmation_header" })
      : intl.formatMessage({ id: "mfa.myendress.enable_confirmation_header" });

    const modelConfirmationMessage = isMFAEnabled
      ? intl.formatMessage({ id: "mfa.myendress.disable_confirmation_message" })
      : intl.formatMessage({ id: "mfa.myendress.enable_confirmation_message" });

    const changeButton = (
      <EditActionButton
        label={mfaLabel}
        id="edit-mfa"
        onClick={() => setShowModel(true)}
      />
    );

    const renderManageMultiFactorAuthentication = (
      <Row className="mfa-details-item">
        <Column className="mfa-row col-xs-8">{message}</Column>
        <Column className="mfa-config-button">{changeButton}</Column>
      </Row>
    );

    return (
      <Loader loading={isLoading}>
        <div className="mfa-details">
          <Modal
            show={showModel}
            onHide={() => setShowModel(false)}
            restoreFocus={false}
            backdrop="static"
          >
            <Modal.Header>
              <Modal.Title>{modelConfirmationHeader}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Loader loading={isSubmitting}>{modelConfirmationMessage}</Loader>
            </Modal.Body>
            <Modal.Footer>
              <SubmitButton
                id="confirm-mfa-change"
                onClick={() => handleMFAChange(mfaLabel)}
                text={intl.formatMessage({
                  id: "mfa.myendress.confirm_button",
                })}
                disabled={isSubmitting}
              />
              <button
                type="button"
                id="cancel-mfa-change"
                onClick={() => setShowModel(false)}
                className="btn btn-default"
                disabled={isSubmitting}
              >
                {intl.formatMessage({ id: "mfa.myendress.cancel_button" })}
              </button>
            </Modal.Footer>
          </Modal>
          {/* @ts-expect-error TS2322: ActionBar in commons lacks the id prop type definition */}
          <ActionBar id="mfa-myendress_header">
            <h2 id="change-current-password-header">
              <FormattedMessage id="mfa.header" />
            </h2>
          </ActionBar>
          {!isLoading && disabledTip}
          {!isLoading && renderManageMultiFactorAuthentication}
        </div>
      </Loader>
    );
  }
  return null;
}

export default withFlipper(
  withSession(
    withNotifier(withApi(withBackend(withIntl(MyEndressMfaDetails)))),
  ),
);
